import React from 'react';
import { useCredits } from '@/hooks/useCredits';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, History, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      { name: 'Browse all AI tools', included: true },
      { name: 'Basic search', included: true },
      { name: 'Save favorites', included: true },
      { name: 'Side-by-side comparison', included: true },
      { name: 'AI-powered recommendations', included: false },
      { name: 'Smart Sort', included: false },
      { name: 'Deep Comparison reports', included: false },
      { name: 'Priority support', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19.99',
    period: '/month',
    popular: true,
    features: [
      { name: 'Browse all AI tools', included: true },
      { name: 'Basic search', included: true },
      { name: 'Save favorites', included: true },
      { name: 'Side-by-side comparison', included: true },
      { name: 'AI-powered recommendations', included: true },
      { name: 'Smart Sort', included: true },
      { name: 'Deep Comparison reports', included: true },
      { name: 'Priority support', included: false },
    ],
  },
  {
    id: 'team',
    name: 'Team',
    price: '$49.99',
    period: '/month',
    features: [
      { name: 'Browse all AI tools', included: true },
      { name: 'Basic search', included: true },
      { name: 'Save favorites', included: true },
      { name: 'Side-by-side comparison', included: true },
      { name: 'AI-powered recommendations', included: true },
      { name: 'Smart Sort', included: true },
      { name: 'Deep Comparison reports', included: true },
      { name: 'Priority support', included: true },
    ],
  },
];

const Credits = () => {
  const { credits, loading } = useCredits();
  const { user, session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handlePurchase = async (planId: string, price: number) => {
    if (!user || !session) {
      toast({ title: 'Authentication required', description: 'Please sign in to upgrade.', variant: 'destructive' });
      navigate('/auth');
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan: planId, price, type: 'subscription' },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (error) {
      console.error('Purchase failed:', error);
      toast({ title: 'Purchase failed', description: 'Failed to initiate purchase. Please try again.', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" /> <span>Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Header */}
          <div className="text-center space-y-3">
            <h1 className="text-4xl font-bold">Choose Your Plan</h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Unlock premium AI features to supercharge your workflow.
            </p>
          </div>

          {/* Current Balance */}
          {!loading && credits && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>Your Balance</span>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/credit-history"><History className="w-4 h-4 mr-2" /> History</Link>
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">{credits.credits_remaining}</span>
                  <span className="text-muted-foreground">credits remaining</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Tier: {credits.subscription_tier}</p>
              </CardContent>
            </Card>
          )}

          {/* Pricing Table */}
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.popular ? 'border-accent border-2' : 'border-border'}`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2.5 text-sm">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {f.included ? (
                          <Check className="w-4 h-4 text-foreground shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                        )}
                        <span className={f.included ? '' : 'text-muted-foreground/60'}>{f.name}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${plan.popular ? 'bg-accent text-accent-foreground hover:bg-accent/90' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => plan.id !== 'free' && handlePurchase(plan.id, parseFloat(plan.price.replace('$', '')))}
                    disabled={plan.id === 'free'}
                  >
                    {plan.id === 'free' ? 'Current Plan' : 'Upgrade to ' + plan.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Credits;
